import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlus, 
  Users, 
  DollarSign, 
  Shield, 
  TrendingUp, 
  Zap, 
  ArrowRight, 
  HelpCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './TransformYourSavings_mobile.css';

// Organic blob backgrounds for visual interest
const OrganicBlobs = () => (
  <div className="transform-savings-blobs">
    <div className="transform-blob blob-1"></div>
    <div className="transform-blob blob-2"></div>
    <div className="transform-blob blob-3"></div>
  </div>
);

// Enhanced background particles for depth
const EnhancedParticles = () => (
  <div className="transform-particles">
    {[...Array(15)].map((_, i) => (
      <div 
        key={i} 
        className={`transform-particle particle-${i % 5}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 4 + 1}px`,
          height: `${Math.random() * 4 + 1}px`,
          animationDelay: `${Math.random() * 8}s`,
          animationDuration: `${Math.random() * 15 + 10}s`
        }}
      />
    ))}
  </div>
);

// Animated process step with interactive hover/tap effects
const ProcessStep = ({ icon, title, description, index }) => {
  return (
    <motion.div
      className="transform-savings-process-step"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileTap={{ scale: 0.98 }}
    >
      <motion.div 
        className="transform-savings-process-icon"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {icon}
        <div className="icon-pulse"></div>
      </motion.div>
      
      <div className="transform-savings-process-content">
        <h4 className="transform-savings-step-title">{title}</h4>
        <p className="transform-savings-step-description">{description}</p>
      </div>
    </motion.div>
  );
};

// Enhanced benefit card with glassmorphism and motion effects - optimized for carousel
const BenefitCard = ({ icon, title, description, isActive }) => {
  return (
    <motion.div
      className="transform-savings-benefit-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: isActive ? 1 : 0.7, 
        y: 0,
        scale: isActive ? 1 : 0.95
      }}
      transition={{ duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="card-shine"></div>
      <div className="card-noise"></div>
      
      <motion.div 
        className="transform-savings-benefit-icon"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {icon}
        <div className="icon-glow"></div>
      </motion.div>
      
      <h4 className="transform-savings-benefit-title">{title}</h4>
      <p className="transform-savings-benefit-description">{description}</p>
    </motion.div>
  );
};

// Enhanced FAQ preview with interactive animations
const FAQPreview = ({ isActive }) => {
  return (
    <motion.div
      className="transform-savings-benefit-card transform-savings-faq-preview"
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: isActive ? 1 : 0.7, 
        y: 0,
        scale: isActive ? 1 : 0.95
      }}
      transition={{ duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="card-shine"></div>
      <div className="card-noise"></div>
      
      <motion.div 
        className="transform-savings-benefit-icon"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <HelpCircle />
        <div className="icon-glow"></div>
      </motion.div>
      
      <h4 className="transform-savings-benefit-title">Frequently Asked Questions</h4>
      <p className="transform-savings-benefit-description">
        Find answers to common questions about joining circles, payment details, and maximizing your savings.
      </p>
      
      <motion.button
        className="transform-savings-faq-link"
        whileHover={{ x: 5 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {/* Add navigation to full FAQ page */}}
      >
        View All FAQs
        <motion.span
          className="arrow-container"
        >
          <ArrowRight size={16} className="arrow-icon" />
        </motion.span>
      </motion.button>
    </motion.div>
  );
};

// Enhanced CTA button with animated effects
const EnhancedCTAButton = ({ children }) => {
  return (
    <motion.button
      className="transform-savings-cta-button"
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: 0.3 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <span className="button-text">{children}</span>
      <motion.span 
        className="button-icon"
        whileHover={{ x: 5 }}
      >
        <ArrowRight size={18} />
      </motion.span>
      <div className="button-glow"></div>
    </motion.button>
  );
};

// Carousel navigation buttons
const CarouselNavButton = ({ direction, onClick, disabled }) => (
  <motion.button
    className={`carousel-nav-button ${direction} ${disabled ? 'disabled' : ''}`}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    onClick={onClick}
    disabled={disabled}
  >
    {direction === 'prev' ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
  </motion.button>
);

// Carousel indicators/dots
const CarouselIndicators = ({ total, current, onSelect }) => (
  <div className="carousel-indicators">
    {Array.from({ length: total }).map((_, index) => (
      <motion.button
        key={index}
        className={`carousel-indicator ${index === current ? 'active' : ''}`}
        whileTap={{ scale: 0.9 }}
        onClick={() => onSelect(index)}
      />
    ))}
  </div>
);

// Mobile-optimized BenefitsCarousel
const BenefitsCarousel = ({ benefits }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  
  const handleNext = () => {
    if (currentIndex < benefits.length) {
      setCurrentIndex(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  // Add touch swipe detection for mobile
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    
    let startX;
    let isDragging = false;
    
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    };
    
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const currentX = e.touches[0].clientX;
      const diff = startX - currentX;
      
      // Prevent scrolling the page while swiping
      if (Math.abs(diff) > 5) {
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e) => {
      if (!isDragging) return;
      const currentX = e.changedTouches[0].clientX;
      const diff = startX - currentX;
      
      if (diff > 50) { // Swipe left
        handleNext();
      } else if (diff < -50) { // Swipe right
        handlePrev();
      }
      
      isDragging = false;
    };
    
    carousel.addEventListener('touchstart', handleTouchStart, { passive: false });
    carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
    carousel.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentIndex]);
  
  // All items to display in the carousel
  const allItems = [...benefits, { type: 'faq' }];
  
  return (
    <div className="benefits-carousel-container">
      <div className="carousel-header">
        <div className="carousel-navigation">
          <CarouselNavButton 
            direction="prev" 
            onClick={handlePrev} 
            disabled={currentIndex === 0}
          />
          <CarouselIndicators 
            total={allItems.length} 
            current={currentIndex} 
            onSelect={setCurrentIndex}
          />
          <CarouselNavButton 
            direction="next" 
            onClick={handleNext} 
            disabled={currentIndex === allItems.length - 1}
          />
        </div>
      </div>
      
      <div className="benefits-carousel" ref={carouselRef}>
        <motion.div 
          className="carousel-track"
          animate={{ x: `-${currentIndex * 100}%` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {benefits.map((benefit, index) => (
            <div className="carousel-item" key={index}>
              <BenefitCard 
                {...benefit} 
                isActive={currentIndex === index}
              />
            </div>
          ))}
          <div className="carousel-item">
            <FAQPreview isActive={currentIndex === allItems.length - 1} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Mobile-optimized version of TransformYourSavings
const TransformYourSavingsMobile = () => {
  const processSteps = [
    { 
      icon: <UserPlus size={20} />, 
      title: "1. Join or Create a Circle", 
      description: "Sign up in minutes and either join an existing circle with open spots or create your own custom savings group." 
    },
    { 
      icon: <Users size={20} />, 
      title: "2. Grow Your Circle", 
      description: "Invite friends or join with strangers – the more members in your circle, the greater the savings for everyone." 
    },
    { 
      icon: <DollarSign size={20} />, 
      title: "3. Unlock Premium Discounts", 
      description: "Instantly access carrier multi-line discounts that are typically only available to families." 
    },
    { 
      icon: <Shield size={20} />, 
      title: "4. Enjoy Worry-Free Service", 
      description: "Our secure payment system and dedicated support team ensure a smooth experience for all members." 
    }
  ];

  const benefits = [
    { 
      icon: <TrendingUp size={20} />, 
      title: "Save Up to $540 Annually", 
      description: "Most members save $40-$45 monthly compared to individual plans – that's hundreds of dollars back in your pocket every year." 
    },
    { 
      icon: <Zap size={20} />, 
      title: "Premium Features, Lower Prices", 
      description: "Get unlimited data, 5G access, and premium features typically reserved for expensive individual plans." 
    },
    { 
      icon: <Users size={20} />, 
      title: "Join Any Circle, Anytime", 
      description: "No contracts or commitments – easily switch between circles as your needs change, with no penalties or fees." 
    }
  ];

  return (
    <section className="transform-savings-wrapper">
      {/* Visual enhancement elements */}
      <div className="noise-overlay"></div>
      <OrganicBlobs />
      <EnhancedParticles />
      <div className="gradient-overlay"></div>
      
      <div className="transform-savings-container">
        {/* Section header with animated title */}
        <motion.div className="transform-savings-header">
          <div className="section-title-container">
            <motion.h2 
              className="transform-savings-section-title"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              How <span className="gradient-text">Circledin</span> Transforms Your Savings
            </motion.h2>
            <div className="title-underline"></div>
          </div>
          
          <motion.h3 
            className="transform-savings-section-subtitle"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Stop overpaying for phone service. Join the group savings revolution today!
          </motion.h3>
        </motion.div>
        
        {/* Journey to Savings section */}
        <motion.div className="transform-savings-journey">
          <motion.h3 
            className="transform-savings-subsection-title"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            Your Journey to Savings
          </motion.h3>
          
          <div className="transform-savings-process-timeline">
            {processSteps.map((step, index) => (
              <ProcessStep key={index} {...step} index={index} />
            ))}
          </div>
        </motion.div>
        
        {/* Benefits with carousel */}
        <motion.div className="transform-savings-benefits">
          <motion.h3 
            className="transform-savings-subsection-title"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            Why Circledin Works for You
          </motion.h3>
          
          <BenefitsCarousel benefits={benefits} />
        </motion.div>
        
        {/* CTA button */}
        <div className="transform-savings-cta">
          <EnhancedCTAButton>
            Start Saving Today
          </EnhancedCTAButton>
        </div>
      </div>
    </section>
  );
};

export default TransformYourSavingsMobile;